import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Lock,
  RefreshCw,
  ShieldCheck,
  Upload,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { Card, Button } from './index';
import {
  AllocatieModus,
  Freshness,
  ImportResultaat,
  ImportRij,
  KolomMapping,
  PAYROLL_QUERY_KEY,
  VERZUIM_QUERY_KEY,
  geslachtCode,
  getPayrollStatus,
  hashEmailLokaal,
  leeftijdsband,
  importeerPayroll,
} from '../services/payrollBridgeApi';
import { PORTAAL_QUERY_KEY } from '../pages/EmployerMedewerkersportaalPage';
import { useAuthStore } from '../store/authStore';
import { medewerkerHash } from '../utils/naamGrendel';

/**
 * Payroll Bridge — 3-staps wizard (Mijn Cockpit, tab 7).
 *
 * Zero-Knowledge: het bestand wordt volledig in de browser gelezen (SheetJS).
 * Alleen de door HR aangewezen kolommen worden gebruikt; het e-mailadres wordt
 * lokaal gehasht (SHA-256) en de budgetten worden lokaal berekend. Naar de
 * server gaan uitsluitend: hash, FTE, afdelingslabel, de twee potten en (optioneel,
 * migratie 181) geslacht en leeftijdsband. Een geboortedatum wordt lokaal een band.
 * BSN, achternamen en ruwe salarissen verlaten dit scherm nooit.
 */

const eur = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const MAX_BESTAND_MB = 10;

interface Kolom {
  index: number;
  letter: string;
  naam: string;
}

interface Bestand {
  naam: string;
  kolommen: Kolom[];
  rijen: unknown[][];
}

const kolomLetter = (i: number) => {
  let s = '';
  let n = i;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
};

/** Europese getallen: "1.234,56", "€ 500", "0,8" → number. */
export function parseGetal(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined) return null;
  let s = String(v).replace(/[€\s]/g, '').trim();
  if (!s) return null;
  if (s.includes(',') && s.includes('.')) s = s.replace(/\./g, '').replace(',', '.');
  else if (s.includes(',')) s = s.replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Kolom-suggestie op basis van de kolomkop (Nmbrs/AFAS/Raet/Loket-conventies). */
function suggereer(kolommen: Kolom[], patroon: RegExp): string | null {
  const hit = kolommen.find((k) => patroon.test(k.naam.toLowerCase()));
  return hit ? hit.naam : null;
}

function FreshnessBadge({ f }: { f: Freshness }) {
  const { t } = useTranslation('employer');
  if (f.niveau === 'geen') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 text-xs font-medium">{t('payrollBridge.freshGeen', 'Nog geen import')}</span>;
  }
  const stijl =
    f.niveau === 'verlopen'
      ? 'bg-red-100 text-red-800'
      : f.niveau === 'waarschuwing'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-green-100 text-green-800';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${stijl}`}>
      {f.niveau === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {t('payrollBridge.freshDagen', { defaultValue: 'Personeelsdata {{dagen}} dagen oud', dagen: f.dagen_oud })}
    </span>
  );
}

export const PayrollBridgeWizard: React.FC = () => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // Import is hoofdgebruiker-only, dus user.id = employer_id (nodig voor de Ziekmeldings-Wizard-hash).
  const employerId = user?.role === 'opdrachtgever' ? user.id : null;
  const { data: status, isLoading } = useQuery({ queryKey: PAYROLL_QUERY_KEY, queryFn: getPayrollStatus });

  const [stap, setStap] = useState<1 | 2 | 3>(1);
  const [bestand, setBestand] = useState<Bestand | null>(null);
  const [leesFout, setLeesFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  const [sleep, setSleep] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [mapping, setMapping] = useState<KolomMapping>({ email: null, fte: null, afdeling: null, salaris: null, pro_budget: null, hobby_budget: null, geslacht: null, geboortedatum: null });
  const [modus, setModus] = useState<AllocatieModus>('vast');
  const [proVast, setProVast] = useState('500');
  const [hobbyVast, setHobbyVast] = useState('150');
  const [proPct, setProPct] = useState('1,0');
  const [hobbyPct, setHobbyPct] = useState('0,3');
  const [fteWegen, setFteWegen] = useState(true);
  const [resultaat, setResultaat] = useState<ImportResultaat | null>(null);

  // Config van de vorige import als startwaarden (alleen kolomNAMEN, geen data)
  React.useEffect(() => {
    const c = status?.config;
    if (!c) return;
    setModus(c.allocatie_modus);
    if (c.pro_vast_bedrag !== null) setProVast(String(c.pro_vast_bedrag));
    if (c.hobby_vast_bedrag !== null) setHobbyVast(String(c.hobby_vast_bedrag));
    if (c.pro_pct !== null) setProPct(String(c.pro_pct).replace('.', ','));
    if (c.hobby_pct !== null) setHobbyPct(String(c.hobby_pct).replace('.', ','));
  }, [status?.config]);

  const leesBestand = useCallback(
    async (file: File) => {
      setLeesFout(null);
      setResultaat(null);
      if (file.size > MAX_BESTAND_MB * 1024 * 1024) {
        setLeesFout(t('payrollBridge.teGroot', { defaultValue: 'Bestand is groter dan {{mb}} MB.', mb: MAX_BESTAND_MB }));
        return;
      }
      if (!/\.(xlsx|xls|csv)$/i.test(file.name)) {
        setLeesFout(t('payrollBridge.verkeerdType', 'Alleen .xlsx, .xls of .csv wordt ondersteund.'));
        return;
      }
      setBezig(true);
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array', raw: false, cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) throw new Error('Geen werkblad gevonden.');
        const matrix = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, defval: '', blankrows: false });
        // Eerste rij met ≥ 2 gevulde cellen = kolomkoppen
        const kopIdx = matrix.findIndex((r) => r.filter((c) => String(c ?? '').trim() !== '').length >= 2);
        if (kopIdx < 0) throw new Error('Geen kolomkoppen gevonden in het bestand.');
        const kop = matrix[kopIdx];
        const kolommen: Kolom[] = kop
          .map((c, i) => ({ index: i, letter: kolomLetter(i), naam: String(c ?? '').trim() }))
          .filter((k) => k.naam !== '');
        const rijen = matrix.slice(kopIdx + 1).filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
        if (rijen.length === 0) throw new Error('Het bestand bevat geen datarijen.');
        setBestand({ naam: file.name, kolommen, rijen });
        setMapping({
          email: suggereer(kolommen, /e-?mail|mailadres/),
          fte: suggereer(kolommen, /fte|dienstverband|parttime|contractomvang|deeltijd/),
          afdeling: suggereer(kolommen, /afdeling|kostenplaats|organisatie|unit|team|department|cost/),
          salaris: suggereer(kolommen, /salaris|bruto|jaarloon|loon/),
          pro_budget: suggereer(kolommen, /opleiding|pro_?budget|studie/),
          hobby_budget: suggereer(kolommen, /vitaliteit|hobby|persoonlijk/),
          geslacht: suggereer(kolommen, /geslacht|gender|sekse/),
          geboortedatum: suggereer(kolommen, /geboorte|birth|geb\.? ?datum/),
        });
        setStap(2);
      } catch (err) {
        setLeesFout((err as Error).message || t('payrollBridge.leesFout', 'Het bestand kon niet worden gelezen.'));
      } finally {
        setBezig(false);
      }
    },
    [t]
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setSleep(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void leesBestand(f);
  };

  const kolomIndex = (naam: string | null | undefined) => bestand?.kolommen.find((k) => k.naam === naam)?.index ?? -1;

  /** Lokale berekening: alleen het resultaat gaat naar de server. */
  const bouwRijen = useCallback(async (): Promise<{ rijen: ImportRij[]; zonderEmail: number; zonderBudget: number }> => {
    if (!bestand) return { rijen: [], zonderEmail: 0, zonderBudget: 0 };
    const iEmail = kolomIndex(mapping.email);
    const iFte = kolomIndex(mapping.fte);
    const iAfd = kolomIndex(mapping.afdeling);
    const iSal = kolomIndex(mapping.salaris);
    const iPro = kolomIndex(mapping.pro_budget);
    const iHobby = kolomIndex(mapping.hobby_budget);
    const iGeslacht = kolomIndex(mapping.geslacht);
    const iGeboorte = kolomIndex(mapping.geboortedatum);
    const pVast = parseGetal(proVast) ?? 0;
    const hVast = parseGetal(hobbyVast) ?? 0;
    const pPct = parseGetal(proPct) ?? 0;
    const hPct = parseGetal(hobbyPct) ?? 0;

    let zonderEmail = 0;
    let zonderBudget = 0;
    const rijen: ImportRij[] = [];
    for (const r of bestand.rijen) {
      const email = String(r[iEmail] ?? '').trim();
      if (!EMAIL_RE.test(email)) {
        zonderEmail++;
        continue;
      }
      let fte = iFte >= 0 ? parseGetal(r[iFte]) : null;
      if (fte !== null && fte > 1 && fte <= 100) fte = fte / 100;
      if (fte !== null && (fte <= 0 || fte > 1)) fte = null;
      const weging = fteWegen && fte !== null ? fte : 1;
      let pro = 0;
      let hobby = 0;
      if (modus === 'vast') {
        pro = pVast * weging;
        hobby = hVast * weging;
      } else if (modus === 'percentage') {
        const sal = iSal >= 0 ? parseGetal(r[iSal]) : null;
        if (sal === null) zonderBudget++;
        pro = ((sal ?? 0) * pPct) / 100;
        hobby = ((sal ?? 0) * hPct) / 100;
      } else {
        const p = iPro >= 0 ? parseGetal(r[iPro]) : null;
        const h = iHobby >= 0 ? parseGetal(r[iHobby]) : null;
        if (p === null && h === null) zonderBudget++;
        pro = p ?? 0;
        hobby = h ?? 0;
      }
      rijen.push({
        email_hash: await hashEmailLokaal(email),
        // Zelfde identifier als de Ziekmeldings-Wizard: SHA-256(employer_id | e-mail) → FTE-koppeling bij verzuim
        case_hash: employerId ? await medewerkerHash(employerId, email) : null,
        fte: fte !== null ? Math.round(fte * 1000) / 1000 : null,
        afdeling: iAfd >= 0 ? String(r[iAfd] ?? '').trim() || null : null,
        pro_budget: Math.round(Math.max(0, pro) * 100) / 100,
        hobby_budget: Math.round(Math.max(0, hobby) * 100) / 100,
        // Jaarverslag-demografie: alleen de code en de band, nooit de datum zelf.
        geslacht: iGeslacht >= 0 ? geslachtCode(r[iGeslacht]) : null,
        leeftijdsband: iGeboorte >= 0 ? leeftijdsband(r[iGeboorte]) : null,
      });
    }
    return { rijen, zonderEmail, zonderBudget };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestand, mapping, modus, proVast, hobbyVast, proPct, hobbyPct, fteWegen, employerId]);

  const [preview, setPreview] = useState<{ aantal: number; zonderEmail: number; zonderBudget: number; proTotaal: number; hobbyTotaal: number; fteSom: number } | null>(null);
  const [previewBezig, setPreviewBezig] = useState(false);

  const naarStap3 = () => {
    if (!mapping.email) {
      toast.error(t('payrollBridge.emailVerplicht', 'Wijs eerst de kolom met het zakelijke e-mailadres aan.'));
      return;
    }
    setStap(3);
    setPreview(null);
  };

  const berekenPreview = async () => {
    setPreviewBezig(true);
    try {
      const { rijen, zonderEmail, zonderBudget } = await bouwRijen();
      setPreview({
        aantal: rijen.length,
        zonderEmail,
        zonderBudget,
        proTotaal: rijen.reduce((s, r) => s + r.pro_budget, 0),
        hobbyTotaal: rijen.reduce((s, r) => s + r.hobby_budget, 0),
        fteSom: rijen.reduce((s, r) => s + (r.fte ?? 1), 0),
      });
    } finally {
      setPreviewBezig(false);
    }
  };

  const importMutation = useMutation({
    mutationFn: async () => {
      const { rijen } = await bouwRijen();
      if (rijen.length === 0) throw new Error(t('payrollBridge.geenRijen', 'Geen rijen met een geldig e-mailadres gevonden.'));
      return importeerPayroll({
        bestandsnaam: bestand?.naam ?? null,
        kolom_mapping: mapping,
        allocatie: {
          modus,
          pro_vast_bedrag: modus === 'vast' ? parseGetal(proVast) ?? 0 : null,
          hobby_vast_bedrag: modus === 'vast' ? parseGetal(hobbyVast) ?? 0 : null,
          pro_pct: modus === 'percentage' ? parseGetal(proPct) ?? 0 : null,
          hobby_pct: modus === 'percentage' ? parseGetal(hobbyPct) ?? 0 : null,
        },
        rijen,
      });
    },
    onSuccess: (r) => {
      setResultaat(r);
      setBestand(null);
      setStap(1);
      toast.success(t('payrollBridge.importGelukt', { defaultValue: 'Personeelsdata verwerkt: {{n}} nieuw, {{b}} bijgewerkt, {{i}} uitgestroomd.', n: r.nieuw, b: r.bijgewerkt, i: r.geinactiveerd }));
      queryClient.invalidateQueries({ queryKey: PAYROLL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PORTAAL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: VERZUIM_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['employer-wkr'] });
      queryClient.invalidateQueries({ queryKey: ['employer-dashboard-signalen'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || err?.message || t('payrollBridge.importFout', 'Importeren mislukt')),
  });

  const kolomOpties = useMemo(
    () =>
      (bestand?.kolommen ?? []).map((k) => (
        <option key={k.index} value={k.naam}>
          {t('payrollBridge.kolomOptie', { defaultValue: 'Kolom {{letter}} - {{naam}}', letter: k.letter, naam: k.naam })}
        </option>
      )),
    [bestand, t]
  );

  const KolomSelect: React.FC<{ veld: keyof KolomMapping; label: string; vraag: string; doel: string; verplicht?: boolean }> = ({ veld, label, vraag, doel, verplicht }) => (
    <div className="rounded-lg border border-gray-200 p-4 bg-white">
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      <p className="text-sm text-gray-700 mt-1">{vraag}</p>
      <select
        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
        value={mapping[veld] ?? ''}
        onChange={(e) => setMapping((m) => ({ ...m, [veld]: e.target.value || null }))}
        aria-label={label}
      >
        <option value="">{verplicht ? t('payrollBridge.kiesKolom', '— Kies een kolom —') : t('payrollBridge.kiesKolomOptioneel', '— Niet in dit bestand —')}</option>
        {kolomOpties}
      </select>
      <p className="text-xs text-gray-500 mt-2">➔ {doel}</p>
    </div>
  );

  const inputClass = 'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';

  return (
    <div className="space-y-6">
      {/* ── Statuskaart ── */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-[#12284A]" />
              {t('payrollBridge.titel', 'Payroll Bridge: personeelsdata & virtuele budgetten')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t('payrollBridge.intro', 'Upload elk kwartaal uw ruwe HR- of salarisexport. De browser filtert de data lokaal; alleen een anonieme hash, de FTE, de afdeling en de twee virtuele potten bereiken BeLagom.')}
            </p>
          </div>
          {status && <FreshnessBadge f={status.freshness} />}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-4"><Loader2 className="w-4 h-4 animate-spin" />{t('payrollBridge.laden', 'Status laden…')}</div>
        ) : status ? (
          <>
            {status.freshness.niveau === 'waarschuwing' && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('payrollBridge.waarschuwingTitel', { defaultValue: 'Actie vereist: uw personeelsdata is {{dagen}} dagen oud.', dagen: status.freshness.dagen_oud })}</p>
                  <p>{t('payrollBridge.waarschuwingTekst', 'Ververs uw data om te zorgen dat de FTE-wegingen van uw verzuimcijfers en de medewerker-wallets perfect compliant blijven lopen voor het nieuwe kwartaal.')}</p>
                </div>
              </div>
            )}
            {status.freshness.niveau === 'verlopen' && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 flex gap-2">
                <Lock className="w-5 h-5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('payrollBridge.lockTitel', 'DATA-LOCK: personeelslijst is verlopen.')}</p>
                  <p>{t('payrollBridge.lockTekst', 'Nieuwe maandsnapshots kunnen pas berekend worden zodra de actuele FTE-omvang per afdeling is ververst. Upload uw nieuwste export om de grafieken te ontgrendelen.')}</p>
                </div>
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />{t('payrollBridge.statActief', 'Actieve medewerkers')}</p>
                <p className="text-xl font-bold text-gray-900">{status.wallets.actief}</p>
                <p className="text-xs text-gray-500">{t('payrollBridge.statFte', { defaultValue: '{{fte}} FTE · {{g}} gekoppeld aan een account', fte: status.wallets.fte_som.toLocaleString('nl-NL'), g: status.wallets.gekoppeld_aan_account })}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500">{t('payrollBridge.statUitgestroomd', 'Uitgestroomd (inactief)')}</p>
                <p className="text-xl font-bold text-gray-900">{status.wallets.inactief}</p>
                <p className="text-xs text-gray-500">{t('payrollBridge.statUitgestroomdSub', 'saldo hard op € 0,00')}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Wallet className="w-3.5 h-3.5" />{t('payrollBridge.statPro', 'Pot 1: professioneel')}</p>
                <p className="text-xl font-bold text-gray-900">{eur.format(status.wallets.pro_resterend)}</p>
                <p className="text-xs text-gray-500">{t('payrollBridge.statVan', { defaultValue: 'resterend van {{totaal}} · gerichte vrijstelling', totaal: eur.format(status.wallets.pro_totaal) })}</p>
              </div>
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-gray-500 flex items-center gap-1"><Wallet className="w-3.5 h-3.5" />{t('payrollBridge.statHobby', 'Pot 2: persoonlijk / hobby')}</p>
                <p className="text-xl font-bold text-gray-900">{eur.format(status.wallets.hobby_resterend)}</p>
                <p className="text-xs text-gray-500">{t('payrollBridge.statVanHobby', { defaultValue: 'resterend van {{totaal}} · WKR vrije ruimte', totaal: eur.format(status.wallets.hobby_totaal) })}</p>
              </div>
            </div>
            {status.config?.laatste_import_op && (
              <p className="text-xs text-gray-500 mt-3">
                {t('payrollBridge.laatsteImport', {
                  defaultValue: 'Laatste import: {{datum}} ({{aantal}} rijen{{bestand}}).',
                  datum: new Date(status.config.laatste_import_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }),
                  aantal: status.config.laatste_import_aantal ?? 0,
                  bestand: status.config.laatste_import_bestandsnaam ? `, ${status.config.laatste_import_bestandsnaam}` : '',
                })}
              </p>
            )}
            {status.afdelingen.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-gray-900 mb-2">{t('payrollBridge.afdelingen', 'Afdelingen uit de export')}</p>
                <div className="flex flex-wrap gap-2">
                  {status.afdelingen.map((a) => (
                    <span
                      key={`${a.label}-${a.department_id}`}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs ${a.gekoppeld ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'}`}
                      title={a.gekoppeld ? undefined : t('payrollBridge.afdelingNietGekoppeld', 'Geen afdeling met deze naam in uw organisatieprofiel: maak deze aan onder Organisatieprofiel om per afdeling te filteren.')}
                    >
                      {a.label ?? '—'} · {a.aantal}
                      {a.onder_privacy_grendel && <Lock className="w-3 h-3" aria-label={t('payrollBridge.privacyGrendel', 'onder de privacy-grendel (< 10)')} />}
                      {!a.gekoppeld && <AlertTriangle className="w-3 h-3" />}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </Card>

      {resultaat && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
          <p className="font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />{t('payrollBridge.resultaatTitel', 'Import verwerkt')}</p>
          <p className="mt-1">
            {t('payrollBridge.resultaatTekst', {
              defaultValue: '{{n}} nieuwe hashes, {{b}} bijgewerkt, {{i}} uitgestroomd (status INACTIVE, saldo € 0,00), {{o}} rijen overgeslagen.',
              n: resultaat.nieuw, b: resultaat.bijgewerkt, i: resultaat.geinactiveerd, o: resultaat.overgeslagen,
            })}
          </p>
          {resultaat.afdelingen_aangemaakt.length > 0 && (
            <p className="mt-1">
              {t('payrollBridge.resultaatAangemaakt', { defaultValue: 'Nieuw aangemaakt in uw organisatieprofiel: {{lijst}}', lijst: resultaat.afdelingen_aangemaakt.join(', ') })}
            </p>
          )}
          {resultaat.afdelingen_niet_gekoppeld.length > 0 && (
            <p className="mt-1 text-amber-800">
              {t('payrollBridge.resultaatAfdelingen', { defaultValue: 'Niet gekoppeld aan uw organisatieprofiel: {{lijst}}', lijst: resultaat.afdelingen_niet_gekoppeld.join(', ') })}
            </p>
          )}
          {resultaat.fouten.length > 0 && <p className="mt-1 text-amber-800">{resultaat.fouten.slice(0, 5).join(' ')}</p>}
        </div>
      )}

      {/* ── Wizard ── */}
      <Card>
        <ol className="flex items-center gap-2 text-xs font-medium mb-5">
          {[1, 2, 3].map((n) => (
            <li key={n} className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center ${stap === n ? 'bg-[#12284A] text-white' : stap > n ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`}>{n}</span>
              <span className={stap === n ? 'text-gray-900' : 'text-gray-500'}>
                {n === 1 ? t('payrollBridge.stap1', 'Ruwe Excel-upload') : n === 2 ? t('payrollBridge.stap2', 'Kolommen aanwijzen') : t('payrollBridge.stap3', 'Budget-allocatie')}
              </span>
              {n < 3 && <span className="w-6 h-px bg-gray-300" />}
            </li>
          ))}
        </ol>

        {stap === 1 && (
          <div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setSleep(true); }}
              onDragLeave={() => setSleep(false)}
              onDrop={onDrop}
              className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${sleep ? 'border-[#12284A] bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}`}
            >
              {bezig ? <Loader2 className="w-10 h-10 mx-auto text-[#12284A] animate-spin" /> : <Upload className="w-10 h-10 mx-auto text-[#12284A]" />}
              <p className="mt-3 text-base font-medium text-gray-900">
                📁 {t('payrollBridge.dropTekst', 'Sleep uw ruwe HR- of Salarisexport hiernaartoe, of klik om te uploaden')}
              </p>
              <p className="text-sm text-gray-500 mt-1">{t('payrollBridge.dropFormaten', 'Ondersteunde bestanden: .xlsx, .xls, .csv')}</p>
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void leesBestand(f); e.target.value = ''; }} />
            </div>
            <p className="text-xs text-gray-600 mt-3 flex gap-2">
              <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
              {t('payrollBridge.privacyTekst', 'U kunt direct een onbewerkte export uit uw salarissysteem (AFAS, Nmbrs, Raet, Loket) uploaden. Onze software filtert de data direct lokaal in uw browser; privacygevoelige gegevens zoals burgerservicenummers of achternamen raken onze database nooit.')}
            </p>
            {leesFout && <p className="mt-3 text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{leesFout}</p>}
          </div>
        )}

        {stap === 2 && bestand && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 text-sm">
              <p className="text-gray-700">
                <FileSpreadsheet className="w-4 h-4 inline mr-1 text-[#12284A]" />
                <span className="font-medium">{bestand.naam}</span> · {t('payrollBridge.gelezen', { defaultValue: '{{k}} kolommen, {{r}} rijen lokaal gelezen', k: bestand.kolommen.length, r: bestand.rijen.length })}
              </p>
              <button type="button" onClick={() => { setBestand(null); setStap(1); }} className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"><X className="w-4 h-4" />{t('payrollBridge.anderBestand', 'Ander bestand')}</button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <KolomSelect veld="email" verplicht label={t('payrollBridge.mapEmailLabel', 'De medewerker-identifier')} vraag={t('payrollBridge.mapEmailVraag', 'Welke kolom bevat het zakelijke e-mailadres van de medewerker?')} doel={t('payrollBridge.mapEmailDoel', 'Wordt lokaal omgezet in een anonieme SHA-256 code waarmee de medewerker later inlogt en zijn wallet ziet.')} />
              <KolomSelect veld="fte" label={t('payrollBridge.mapFteLabel', 'De contractomvang (FTE)')} vraag={t('payrollBridge.mapFteVraag', 'Welke kolom bevat de contractomvang (FTE)?')} doel={t('payrollBridge.mapFteDoel', 'Weegt het budget én de verzuimdagen, zodat een zieke parttimer van 0,2 FTE niet voor 1,0 FTE in de verzuimcijfers drukt.')} />
              <KolomSelect veld="afdeling" label={t('payrollBridge.mapAfdLabel', 'De afdelings-scope')} vraag={t('payrollBridge.mapAfdVraag', 'Welke kolom bevat de afdeling / kostenplaats?')} doel={t('payrollBridge.mapAfdDoel', 'Bedrijfsthermometer en verzuimstatistieken per afdeling, volledig anoniem (harde minimum-10 privacygrendel). Onbekende afdelingen worden automatisch in uw organisatieprofiel aangemaakt.')} />
              <KolomSelect veld="geslacht" label={t('payrollBridge.mapGeslachtLabel', 'Geslacht (optioneel)')} vraag={t('payrollBridge.mapGeslachtVraag', 'Welke kolom bevat het geslacht?')} doel={t('payrollBridge.mapGeslachtDoel', 'Alleen voor de anonieme demografie in het jaarverslag (subgroepen onder 10 worden samengevoegd); los van elke melding.')} />
              <KolomSelect veld="geboortedatum" label={t('payrollBridge.mapGeboorteLabel', 'Geboortedatum (optioneel)')} vraag={t('payrollBridge.mapGeboorteVraag', 'Welke kolom bevat de geboortedatum?')} doel={t('payrollBridge.mapGeboorteDoel', 'Wordt in uw browser omgezet in een leeftijdsband (<25, 25–40, 41–55, >55); de datum zelf verlaat dit scherm nooit.')} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setStap(1)}>{t('payrollBridge.terug', 'Terug')}</Button>
              <Button type="button" onClick={naarStap3}>{t('payrollBridge.verder', 'Verder naar budget-allocatie')}</Button>
            </div>
          </div>
        )}

        {stap === 3 && bestand && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">{t('payrollBridge.allocatieIntro', 'Kies hoe de virtuele saldi per anonieme hash berekend worden. Alles wordt lokaal berekend; salarissen verlaten uw browser niet.')}</p>
            <div className="space-y-2">
              {(['vast', 'percentage', 'maatwerk'] as AllocatieModus[]).map((m) => (
                <label key={m} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${modus === m ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                  <input type="radio" name="payroll-modus" className="mt-1 text-blue-600 focus:ring-blue-500" checked={modus === m} onChange={() => setModus(m)} />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">
                      {m === 'vast' ? t('payrollBridge.optieA', 'Optie A: Vaste bedragen (Enterprise-standaard)') : m === 'percentage' ? t('payrollBridge.optieB', 'Optie B: Percentage van het salaris (dynamisch, FTE-gewogen)') : t('payrollBridge.optieC', 'Optie C: Maatwerk per medewerker (direct uit Excel)')}
                    </span>
                    {modus === m && m === 'vast' && (
                      <span className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-2">{t('payrollBridge.proWallet', 'Professioneel wallet')} <input className={`${inputClass} w-28`} value={proVast} onChange={(e) => setProVast(e.target.value)} inputMode="decimal" aria-label="Professioneel wallet" /> €</span>
                        <span className="flex items-center gap-2">{t('payrollBridge.hobbyWallet', 'Hobby wallet')} <input className={`${inputClass} w-28`} value={hobbyVast} onChange={(e) => setHobbyVast(e.target.value)} inputMode="decimal" aria-label="Hobby wallet" /> €</span>
                      </span>
                    )}
                    {modus === m && m === 'percentage' && (
                      <span className="mt-3 block space-y-3 text-sm">
                        <span className="block">
                          <span className="block text-gray-700 mb-1">{t('payrollBridge.mapSalarisVraag', 'Welke kolom bevat het bruto jaarsalaris?')}</span>
                          <select className={`${inputClass} w-full`} value={mapping.salaris ?? ''} onChange={(e) => setMapping((mm) => ({ ...mm, salaris: e.target.value || null }))}>
                            <option value="">{t('payrollBridge.kiesKolom', '— Kies een kolom —')}</option>
                            {kolomOpties}
                          </select>
                        </span>
                        <span className="flex flex-wrap items-center gap-4">
                          <span className="flex items-center gap-2">{t('payrollBridge.proWallet', 'Professioneel wallet')} <input className={`${inputClass} w-20`} value={proPct} onChange={(e) => setProPct(e.target.value)} inputMode="decimal" aria-label="Professioneel percentage" /> %</span>
                          <span className="flex items-center gap-2">{t('payrollBridge.hobbyWallet', 'Hobby wallet')} <input className={`${inputClass} w-20`} value={hobbyPct} onChange={(e) => setHobbyPct(e.target.value)} inputMode="decimal" aria-label="Hobby percentage" /> %</span>
                        </span>
                      </span>
                    )}
                    {modus === m && m === 'maatwerk' && (
                      <span className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <span className="block">
                          <span className="block text-gray-700 mb-1">{t('payrollBridge.mapProVraag', 'Welke kolom bevat het Pro-budget?')}</span>
                          <select className={`${inputClass} w-full`} value={mapping.pro_budget ?? ''} onChange={(e) => setMapping((mm) => ({ ...mm, pro_budget: e.target.value || null }))}>
                            <option value="">{t('payrollBridge.kiesKolom', '— Kies een kolom —')}</option>
                            {kolomOpties}
                          </select>
                        </span>
                        <span className="block">
                          <span className="block text-gray-700 mb-1">{t('payrollBridge.mapHobbyVraag', 'Welke kolom bevat het Hobby-budget?')}</span>
                          <select className={`${inputClass} w-full`} value={mapping.hobby_budget ?? ''} onChange={(e) => setMapping((mm) => ({ ...mm, hobby_budget: e.target.value || null }))}>
                            <option value="">{t('payrollBridge.kiesKolom', '— Kies een kolom —')}</option>
                            {kolomOpties}
                          </select>
                        </span>
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {modus !== 'maatwerk' && mapping.fte && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={fteWegen} onChange={(e) => setFteWegen(e.target.checked)} />
                {t('payrollBridge.fteWegen', 'Bedragen wegen naar FTE (0,8 FTE ontvangt 80% van het bedrag)')}
              </label>
            )}

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
              <p className="font-semibold text-gray-900 flex items-center gap-2"><Lock className="w-4 h-4" />{t('payrollBridge.grendelTitel', 'Harde Fiscale Grendel')}</p>
              <ul className="mt-2 space-y-1 text-gray-700 list-disc pl-5">
                <li>{t('payrollBridge.grendelPro', 'Pot 1 (professioneel): Mentale Kracht, Fysieke Fitheid en Professionele Groei — gerichte vrijstelling, 100% belastingvrij, drukt niet op de WKR-vrije ruimte.')}</li>
                <li>{t('payrollBridge.grendelHobby', 'Pot 2 (persoonlijk/hobby): Persoonlijke Passie & Hobby’s — WKR vrije ruimte. Uitgaven worden onherroepelijk van deze pot afgeschreven.')}</li>
                <li>{t('payrollBridge.grendelVirtueel', 'Geen pre-paid: er staat geen geld vast. Facturatie via Stripe Connect vindt pas achteraf plaats zodra een medewerker een tegel activeert; is de pot leeg, dan weigert de software de inkoop. Budget kan niet tussen de potten weglekken.')}</li>
              </ul>
            </div>

            {preview && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                <p className="font-semibold">{t('payrollBridge.previewTitel', 'Controle vóór verzending')}</p>
                <p className="mt-1">
                  {t('payrollBridge.previewTekst', {
                    defaultValue: '{{n}} medewerkers · {{fte}} FTE · Pot 1 totaal {{pro}} · Pot 2 totaal {{hobby}}',
                    n: preview.aantal, fte: preview.fteSom.toLocaleString('nl-NL', { maximumFractionDigits: 2 }), pro: eur.format(preview.proTotaal), hobby: eur.format(preview.hobbyTotaal),
                  })}
                </p>
                {preview.zonderEmail > 0 && <p className="mt-1 text-amber-800">{t('payrollBridge.previewZonderEmail', { defaultValue: '{{n}} rijen zonder geldig e-mailadres worden overgeslagen.', n: preview.zonderEmail })}</p>}
                {preview.zonderBudget > 0 && <p className="mt-1 text-amber-800">{t('payrollBridge.previewZonderBudget', { defaultValue: '{{n}} rijen zonder leesbaar bedrag krijgen € 0,00.', n: preview.zonderBudget })}</p>}
                <p className="mt-1 text-xs">{t('payrollBridge.previewVerloop', 'Hashes uit een eerdere import die niet in dit bestand voorkomen worden op INACTIVE gezet en hun saldo gaat naar € 0,00.')}</p>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setStap(2)}>{t('payrollBridge.terug', 'Terug')}</Button>
              <Button type="button" variant="secondary" loading={previewBezig} onClick={berekenPreview}>{t('payrollBridge.previewKnop', 'Lokaal berekenen & controleren')}</Button>
              <Button type="button" loading={importMutation.isPending} disabled={!preview || preview.aantal === 0} onClick={() => importMutation.mutate()}>
                <RefreshCw className="w-4 h-4 mr-1" />
                {t('payrollBridge.importKnop', 'Personeelsdata verwerken')}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PayrollBridgeWizard;
